import { Stack, Slot, usePathname, useSegments } from 'expo-router';
import { useIsFocused } from 'expo-router/react-navigation';
import { useEffect } from 'react';
import { Platform, View } from 'react-native';
import Constants from 'expo-constants';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { MobileHeader } from '~/components/layout/MobileHeader';
import { runPendingTabSelection } from '~/components/layout/TabBarSlideMenuButton';
import { ScreenOptionsProvider } from '~/components/layout/ScreenOptionsContext';
import { LargeScreenTitleBar } from '~/components/layout/LargeScreenTitleBar';
import { WebDocumentTitle } from '~/components/layout/WebDocumentTitle';
import { registerMountedTab } from '~/components/layout/tabMountRegistry';

export const unstable_settings = {
  initialRouteName: 'feeds/global',
  feeds: {
    initialRouteName: 'feeds/global',
  },
  people: {
    initialRouteName: 'people/online',
  },
  discover: {
    initialRouteName: 'discover',
  },
  messages: {
    initialRouteName: 'messages/inbox',
  },
  notifications: {
    initialRouteName: 'notifications',
  },
};

export default function InnerTabsLayout() {
  const isLargeScreen = useIsLargeScreen();
  const segments = useSegments();
  const isFocused = useIsFocused();

  // Completes warm cross-tab slide-menu selections: the replace to the
  // selected list runs only once this tab is focused, painted, AND the
  // router's own pathname points inside it — router.replace computes its
  // action against that pathname, and running while it still points at the
  // previous tab turns the replace into a cross-navigator push (back
  // button). segments[1] is this instance's tab group, e.g. '(people)'.
  const pathname = usePathname();
  const tabName = segments[1];
  const tabBasePath = tabName?.startsWith('(') ? `/${tabName.slice(1, -1)}` : `/${tabName}`;

  // Cold/warm ground truth for the slide menus (see tabMountRegistry).
  // Register-only: freezeOnBlur suspends blurred tabs, which runs effect
  // cleanups, so a cleanup here would mark every frozen tab as cold.
  // Community switches reset the (app) tree (resetAppNavigationTree), which
  // clears the registry — a tab is only warm within the tree it mounted under.
  useEffect(() => {
    registerMountedTab(tabName);
  }, [tabName]);
  useEffect(() => {
    if (!isFocused) return;
    if (pathname === tabBasePath || pathname.startsWith(`${tabBasePath}/`)) {
      runPendingTabSelection(tabName);
    }
  }, [isFocused, pathname, tabBasePath, tabName]);

  // Disable animations on emulators/simulators for better performance
  // Real devices (Android/iOS) get smooth animations, emulators get instant transitions
  const isEmulator = !Constants.isDevice;
  const shouldDisableAnimations = isEmulator;

  return (
    <ScreenOptionsProvider>
      <WebDocumentTitle includeNotificationCount />
      {isLargeScreen ? (
        <View className="flex-1">
          <LargeScreenTitleBar />
          {/* Web keeps the lightweight Slot and its own scroll restoration.
              Native large screens use a headerless Stack (title bar lives above
              it) so background screens stay mounted and list scroll position is
              preserved on back — same as mobile, no scroll management needed. */}
          {Platform.OS === 'web' ? (
            <Slot />
          ) : (
            <Stack screenOptions={{ headerShown: false, animation: 'none', freezeOnBlur: true }} />
          )}
        </View>
      ) : (
        <Stack
          screenOptions={{
            headerTintColor: 'white',
            header: (props) => <MobileHeader {...props} />,
            animation: shouldDisableAnimations ? 'none' : 'default',
            freezeOnBlur: true,
          }}
        />
      )}
    </ScreenOptionsProvider>
  );
}
